import { useCallback, useEffect, useRef, useState } from "react";
import { APIError } from "@certn/api";
import { fetcher } from "@certn/api";
import type { components } from "@certn/api/generated-schemas/client-portal";
import { useSWRConfig } from "swr";
import { SubsidiaryChangeModal } from "../components/common/SubsidiaryChangeModal";

type SubsidiaryChangeRequiredResponse =
  components["schemas"]["SubsidiaryChangeRequiredResponse"];

const isSubsidiaryChangeError = (error: unknown): error is APIError => {
  return (
    error instanceof APIError &&
    error.status === 409 &&
    error.body?.code === "SUBSIDIARY_CHANGE_REQUIRED"
  );
};

/**
 * Global interceptor for subsidiary change confirmations.
 *
 * When a Client Portal user updates a billing address to a country that maps
 * to a different Certn subsidiary, the backend returns a 409 response with
 * `code: "SUBSIDIARY_CHANGE_REQUIRED"`. This provider intercepts that response
 * at the `fetcher.submit` level via `fetcher.onSubmitError` — meaning ANY form
 * that submits via `useSubmit` is automatically covered without per-form integration.
 *
 * Flow:
 * 1. Form submits normally (no extra code needed)
 * 2. Backend detects subsidiary mismatch → returns 409 with currency/PP details
 * 3. This provider catches the 409, shows a confirmation modal
 * 4. On confirm: retries the same request with `confirm_subsidiary_change: true`
 * 5. On cancel: re-throws the original 409 (form can suppress via errorIsStatus)
 *
 * Backend contract (`project/actions/billing_address_validation.py`):
 * - Eligibility: SSB source + no invoices/usages + feature flag enabled
 * - 409 body shape: `{ code, is_mismatched, current_currency, new_currency, payment_profiles_to_archive }`
 * - Retry with `confirm_subsidiary_change: true` triggers the actual migration
 */
export function SubsidiaryChangeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { mutate: globalMutate } = useSWRConfig();
  const [data, setData] = useState<SubsidiaryChangeRequiredResponse | null>(
    null
  );
  const pendingRef = useRef<{
    resolve: (confirmed: boolean) => void;
  } | null>(null);

  const refresh = useCallback(() => {
    globalMutate(
      (key) =>
        typeof key === "string" &&
        (key.startsWith("/api/internal/client-portal/me") ||
          key.includes("/billing-profiles/") ||
          key.includes("/billing-profile") ||
          key.includes("/payment-profiles")),
      undefined,
      { revalidate: true }
    );
  }, [globalMutate]);

  const awaitConfirmation = useCallback(
    (responseBody: SubsidiaryChangeRequiredResponse): Promise<boolean> => {
      // Reject any existing pending confirmation to avoid orphaned promises
      pendingRef.current?.resolve(false);

      setData(responseBody);
      return new Promise<boolean>((resolve) => {
        pendingRef.current = { resolve };
      });
    },
    []
  );

  const onConfirm = useCallback(() => {
    pendingRef.current?.resolve(true);
    pendingRef.current = null;
    setData(null);
  }, []);

  const onCancel = useCallback(() => {
    pendingRef.current?.resolve(false);
    pendingRef.current = null;
    setData(null);
  }, []);

  useEffect(() => {
    fetcher.onSubmitError = async (error, retry, { body }) => {
      if (!isSubsidiaryChangeError(error)) {
        throw error;
      }

      const responseBody = error.body as SubsidiaryChangeRequiredResponse;
      const confirmed = await awaitConfirmation(responseBody);

      if (!confirmed) {
        throw error;
      }

      const result = await retry(
        typeof body === "object"
          ? { ...body, confirm_subsidiary_change: true }
          : body
      );

      refresh();

      return result;
    };

    return () => {
      fetcher.onSubmitError = null;
    };
  }, [awaitConfirmation, refresh]);

  return (
    <>
      {children}
      {data && (
        <SubsidiaryChangeModal
          data={data}
          onCancel={onCancel}
          onConfirm={onConfirm}
        />
      )}
    </>
  );
}
